import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useTranslation } from "react-i18next";

import { IMeasurementTypeOptions } from "../../../../../../../@types/Measurements";
import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import { Checkbox } from "../../../../../../../components/Checkbox";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import { useWardLabel } from "../../../../../../../hooks/useWardLabel";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  SelectContainer,
  TextFieldCss,
  ExcludeBlowsContainer,
  popupOptionsCss,
} from "../RuleComponent.styles";

interface IMeasurementFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  ruleConditionComparators?: IWorkflowsSelectOption[];
  ruleConditionTriggers?: IWorkflowsSelectOption[];
  measurementTypes?: IMeasurementTypeOptions[];
}

export function MeasurementFields({
  formik,
  ruleConditionComparators,
  ruleConditionTriggers,
  measurementTypes,
}: IMeasurementFieldsProps) {
  const { t } = useTranslation();

  const wardLabel = useWardLabel();

  const shouldShowErrorMessage = (
    field: `conditions[${number}].${keyof IRuleCondition}`,
    index?: number
  ): string => {
    if (field.startsWith("conditions") && index !== undefined) {
      const key = field.split(".")[1] as keyof IRuleCondition;

      const touchedArray = formik.touched.conditions as
        | FormikTouched<IRuleCondition>[]
        | undefined;
      const errorArray = formik.errors.conditions as
        | FormikErrors<IRuleCondition>[]
        | undefined;

      if (touchedArray && errorArray) {
        const touched = touchedArray[index];
        const error = errorArray[index];

        return touched?.[key] && error?.[key] ? (error[key] as string) : "";
      }

      return "";
    }

    return "";
  };

  const handleMeasurementTypesOptions = () => {
    if (measurementTypes?.length === 0) return [];

    const categoryOrder = [
      "spirometry",
      "measurement",
      "lab_result",
      "treatments",
    ];

    const groupedData = measurementTypes?.reduce(
      (
        accumulator: Record<string, DefaultOptionType>,
        measurementType: IMeasurementTypeOptions
      ) => {
        // Fibricheck
        if (measurementType.measurementType === "Arrhythmia") {
          return accumulator;
        }

        if (!accumulator[measurementType.category]) {
          accumulator[measurementType.category] = {
            label: measurementType.categoryDisplayName,
            title: measurementType.category,
            options: [],
          };
        }

        (accumulator[measurementType.category]?.options as IOption[])?.push({
          label: measurementType.measurementTypeDisplayName,
          value: measurementType.measurementType,
          key: measurementType.measurementType,
        });

        return accumulator;
      },
      {}
    );

    const orderedData = categoryOrder
      ?.filter((category) => groupedData?.[category])
      ?.map((category) => {
        (groupedData?.[category]?.options as IOption[])?.sort(
          (a: IOption, b: IOption) => a.label.localeCompare(b.label)
        );
        return groupedData?.[category];
      }) as DefaultOptionType[];

    return Object.values(orderedData) ?? [];
  };

  const handleRuleTriggerOptions = () => {
    const measurementTypeCategory = measurementTypes?.find(
      (measurementType) =>
        measurementType.measurementType ===
        formik.values.conditions[0].measurementType
    )?.category;

    if (measurementTypeCategory === "spirometry") {
      return (
        ruleConditionTriggers?.map((ruleTrigger) => {
          return {
            label: getWorkflowOptionLabel("trigger", ruleTrigger, t),
            value: ruleTrigger.type,
            key: ruleTrigger.type,
          };
        }) ?? []
      );
    }

    return (
      ruleConditionTriggers
        ?.map((ruleTrigger) => {
          return {
            label: getWorkflowOptionLabel("trigger", ruleTrigger, t),
            value: ruleTrigger.type,
            key: ruleTrigger.type,
          };
        })
        .filter((ruleTrigger) => ruleTrigger.key !== "HighestInSession") ?? []
    );
  };

  const handleThresholdName = () => {
    if (
      formik.values.conditions[0].conditionComparatorType ===
      "PercentageDecreaseFromBaseline"
    ) {
      return t("workflows_rule_percentageDecrease");
    }

    if (
      formik.values.conditions[0].conditionComparatorType ===
      "PercentageIncreaseFromBaseline"
    ) {
      return t("workflows_rule_percentageIncrease");
    }

    return t("workflows_rule_threshold");
  };

  const handleThresholdUnit = () => {
    if (
      formik.values.conditions[0].conditionComparatorType ===
        "PercentageDecreaseFromBaseline" ||
      formik.values.conditions[0].conditionComparatorType ===
        "PercentageIncreaseFromBaseline"
    )
      return "(%)";

    const unit = measurementTypes?.find(
      (measurementType) =>
        measurementType.measurementType ===
        formik.values.conditions[0].measurementType
    )?.unit;

    return unit ? `(${unit})` : null;
  };

  return (
    <>
      <SelectContainer
        isError={
          shouldShowErrorMessage("conditions[0].measurementType", 0) !== ""
        }
      >
        <p>{t("workflows_rule_measurementType")}</p>
        <Select
          placeholder={t("workflows_rule_fvc")}
          defaultValue={formik.values.measurementType}
          suffixIcon={<ArrowDown />}
          options={handleMeasurementTypesOptions()}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noDataForWards", {
            wards: wardLabel.pluralLower,
          })}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].measurementType", value);
            formik.setFieldValue("ruleTrigger", undefined);
          }}
        />
        <ErrorMessage>
          {shouldShowErrorMessage("conditions[0].measurementType", 0)}
        </ErrorMessage>
      </SelectContainer>

      {formik.values.conditions[0].measurementType !== undefined ? (
        <SelectContainer
          isError={
            shouldShowErrorMessage(
              "conditions[0].conditionComparatorType",
              0
            ) !== ""
          }
        >
          <p>{t("workflows_rule_comparator")}</p>
          <SelectDropdown
            placeholder={t("workflows_rule_greaterThan")}
            value={formik.values.conditions[0].conditionComparatorType}
            options={
              ruleConditionComparators?.map((ruleComparator) => {
                return {
                  label: getWorkflowOptionLabel(
                    "comparator",
                    ruleComparator,
                    t
                  ),
                  value: ruleComparator.type,
                  key: ruleComparator.type,
                };
              }) ?? []
            }
            onValueChange={(value) =>
              formik.setFieldValue(
                "conditions[0].conditionComparatorType",
                value as string
              )
            }
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].conditionComparatorType", 0)}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.conditions[0].conditionComparatorType !== undefined ? (
        <>
          <SelectContainer
            isError={
              shouldShowErrorMessage("conditions[0].threshold", 0) !== ""
            }
          >
            <p>
              {handleThresholdName()} {handleThresholdUnit()}
            </p>
            <TextField
              name="conditions[0].threshold"
              placeholder="0"
              value={formik.values.conditions[0].threshold}
              label=""
              onChange={formik.handleChange}
              backgroudColor="white"
              className={`${TextFieldCss()} nopan nodrag`}
              type="number"
            />
            <ErrorMessage>
              {shouldShowErrorMessage("conditions[0].threshold", 0)}
            </ErrorMessage>
          </SelectContainer>

          <SelectContainer
            isError={
              shouldShowErrorMessage("conditions[0].triggerType", 0) !== ""
            }
          >
            <p>{t("workflows_rule_ruleTrigger")}</p>
            <SelectDropdown
              placeholder={t("workflows_rule_anyReading")}
              value={formik.values.conditions[0].triggerType}
              options={handleRuleTriggerOptions()}
              onValueChange={(value) =>
                formik.setFieldValue(
                  "conditions[0].triggerType",
                  value as string
                )
              }
              className={`${selectCss()} nopan nodrag`}
            />
            <ErrorMessage>
              {shouldShowErrorMessage("conditions[0].triggerType", 0)}
            </ErrorMessage>
          </SelectContainer>

          {measurementTypes?.find(
            (x) =>
              x.measurementType === formik.values.conditions[0].measurementType
          )?.category === "spirometry" ? (
            <ExcludeBlowsContainer>
              <Checkbox
                checked={formik.values.conditions[0].excludeBadBlows ?? false}
                onChange={(value) =>
                  formik.setFieldValue("conditions[0].excludeBadBlows", value)
                }
                label=""
              />
              <p>{t("workflows_rule_usableQualityOnly")}</p>
            </ExcludeBlowsContainer>
          ) : null}
        </>
      ) : null}
    </>
  );
}
